import type { Kid, StatusResponse } from '../types';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, { method = 'GET', body, signal }: RequestOptions = {}): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: 'no-store',
    signal,
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // A non-JSON response (a proxy error page, say) falls through to the
    // generic message below.
  }

  if (!response.ok) {
    const message =
      typeof payload === 'object' && payload !== null && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (${response.status}).`;
    throw new ApiError(message, response.status);
  }

  return payload as T;
}

export const api = {
  getStatus: (signal?: AbortSignal) => request<StatusResponse>('/api/status', { signal }),

  getSession: (signal?: AbortSignal) =>
    request<{ authenticated: boolean }>('/api/admin/session', { signal }),

  login: (password: string) =>
    request<{ authenticated: boolean }>('/api/admin/login', {
      method: 'POST',
      body: { password },
    }),

  logout: () => request<{ authenticated: boolean }>('/api/admin/logout', { method: 'POST' }),

  setKid: (slug: string, available: boolean) =>
    request<{ kid: Kid }>(`/api/admin/kids/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: { available },
    }),

  setAll: (available: boolean) =>
    request<{ kids: Kid[] }>('/api/admin/kids', { method: 'POST', body: { available } }),
};
